import {isFunction, each, isArray, merge} from 'lodash';
import {useCallback, useMemo, useState, useRef} from 'react';
import {useInterval} from '@huse/timeout';
import {toast} from 'acud';

import useFetch, {FetchPayload} from '../fetch';
import {getErrorMessage} from '../../../utils/bizUtil';
import {useLocale} from '../../../components/locale';

export interface IRequest {
    action: <TData>(params?: any, options?: any) => Promise<TData>,
    defaultListParams?: any;
    $onRequest?: (payload?: any, extra?: any) => any,
    $onResponse?: (result?: any, params?: any) => any,
    $onError?: (ex: any) => void,
    errorToast?: boolean;
    abort?: boolean;
    interval?: number;
}

export interface ListExtra {
    triggerBy: 'initial' | 'pagination' | 'filter' | 'reload' | 'polling' | 'unknown';
    triggerByType?: 'auto' | 'manual';
    triggerByParams?: any;
}

const useTableFetch = (request: IRequest) => {
    const {action, defaultListParams, $onRequest, $onResponse, $onError, errorToast, abort, interval} = request;

    const [locale] = useLocale('useTable');

    const [listParams, setListParams] = useState(defaultListParams);

    // 用以记录一些请求的额外信息
    const listExtraRef = useRef<ListExtra>({triggerBy: 'initial'});

    const setListExtra = useCallback(listExtra => {
        listExtraRef.current = listExtra;
    }, []);

    const updateListParams = useCallback((listParams: any, listExtra = {triggerBy: 'unknown'}) => {
        setListExtra(listExtra);
        setListParams(listParams);
    }, [setListExtra]);

    const params = useMemo(() => {
        let actualParams = {};
        // 过滤掉空值
        each(listParams, (value, key) => {
            const isEmptyValue = value === '' || value === null || (isArray(value) && value.length === 0);
            if (!isEmptyValue) {
                actualParams[key] = value;
            }
        });
        return actualParams;
    }, [listParams]);

    const $onFetchRequest = useCallback((params, extra) => {
        // 为了不让业务侧直接修改params对象，以免造成数据对比失败，这里做一个浅拷贝（因为对比目前也只是浅层对比）
        const requestParams = {...params};
        const requestPayload = isFunction($onRequest)
            ? ($onRequest(requestParams, merge({}, listExtraRef.current, extra)) || requestParams)
            : requestParams;
        return requestPayload;
    }, [$onRequest]);

    const $onFetchResponse = useCallback((response: any) => {
        const responsePage = isFunction($onResponse)
            ? ($onResponse(response, listParams) || response)
            : response;
        const {totalCount, pageNo, result} = responsePage;
        // 特殊逻辑，当totalCount > 0 但是没有数据的时候，查找上一页, 主要场景：最后一页数据全部删除后的情况下兼容
        if (totalCount > 0 && pageNo > 1 && !result?.length) {
            setListParams((prevListParams: any) => ({...prevListParams, pageNo: pageNo - 1}));
        }
        return responsePage;
    }, [$onResponse, listParams]);

    const $onFetchError = useCallback((err: any) => {
        if (isFunction($onError)) {
            $onError(err);
        }
        const errorMsg = getErrorMessage(err, locale?.failedToGetRecords);
        errorMsg && errorToast && toast.error({
            message: errorMsg,
            duration: 5
        });
    }, [errorToast, locale?.failedToGetRecords, $onError]);

    const fetchResult = useFetch(action, params, $onFetchRequest, $onFetchResponse, $onFetchError, abort);

    // triggerBy: 'polling'：表明是轮询请求
    // isPolling 将来会被废弃，暂时保留用以兼容老代码
    const reloadParams = {needLoading: false, extra: {triggerBy: 'polling', isPolling: true}} as FetchPayload;
    useInterval(() => fetchResult.reload(reloadParams), (interval || -1));

    return {
        listParams,
        setListParams: updateListParams,
        ...fetchResult
    };
};

export default useTableFetch;